import React from 'react';

import { sortBy } from 'lodash';
import SessionGraphs, {
  GraphBlueprint,
  GraphOptions,
  GraphSeries,
  GraphPoint,
  GraphInstanceProps
} from '../index';
import { FormatDate } from '../../../../libs/time';
import { IEpxHeart } from '../../../../libs/apps/epxheart';

interface EpxHeartGraphPoint extends GraphPoint {
  symptoms?: string[];
}

const EpxHeartGraphs: React.FC<GraphInstanceProps> = (props): JSX.Element => {
  const symptoms: GraphBlueprint<IEpxHeart> = {
    name: 'Heart Failure',
    config: {
      chart: {
        type: null
      },
      title: {
        text: 'Heart Failure - Symptoms'
      },
      xAxis: {
        type: 'category',
        tickmarkPlacement: 'on',
        title: {
          text: null
        }
      },
      yAxis: [
        {
          title: {
            text: '# of Symtoms'
          },
          min: 0,
          max: 3,
          tickInterval: 1
        }
      ],
      responsive: {}
    },
    draw: (sessions) => {
      const options: GraphOptions = {
        series: []
      };
      const series: GraphSeries = {
        type: 'line',
        name: 'Symptoms',
        tooltip: {
          pointFormatter: function (this: EpxHeartGraphPoint) {
            if (this.y === null) {
              return 'No Response';
            } else if (this.symptoms.length === 0) {
              return 'None';
            } else {
              return this.symptoms.join(', ');
            }
          }
        }
      };
      const data: EpxHeartGraphPoint[] = [];
      sessions = sortBy(sessions, (session) => session.time);
      for (const [i, session] of sessions.entries()) {
        const time = session.respondedAt || session.time;
        const symptoms = {
          count: 0,
          list: []
        };
        if (session.dpe === -1) {
          symptoms.count++;
          symptoms.list.push('Dyspnea/Pedal Edema');
        }
        if (session.orthopnea === -1) {
          symptoms.count++;
          symptoms.list.push('Orthopnea');
        }
        if (session.hasExcessiveWeightGain) {
          symptoms.count++;
          symptoms.list.push('Excessive Weight Gain');
        }
        if (session.dyspnea === -1) {
          symptoms.count++;
          symptoms.list.push('Dyspnea');
        }
        if (session.edema === -1) {
          symptoms.count++;
          symptoms.list.push('Edema');
        }
        if (session.dpe === null && session.dyspnea === null) {
          symptoms.count = null;
        }
        data.push({
          id: String(session.id),
          name: FormatDate(time, 'MM/DD'),
          y: symptoms.count,
          x: i,
          symptoms: symptoms.list
        });
      }
      series.data = data;
      options.series = [series];
      return options;
    }
  };
  const weight: GraphBlueprint<IEpxHeart> = {
    name: 'Heart Failure',
    config: {
      chart: {
        type: null
      },
      title: {
        text: 'Heart Failure - Weights'
      },
      xAxis: {
        type: 'category',
        tickmarkPlacement: 'on',
        title: {
          text: null
        }
      },
      yAxis: [
        {
          title: {
            text: 'Weight'
          },
          allowDecimals: true
        }
      ]
    },
    draw: (sessions) => {
      const options: GraphOptions = {
        series: []
      };
      const series: GraphSeries = {
        type: 'line',
        name: 'Weight'
      };
      const data: GraphPoint[] = [];
      sessions = sortBy(sessions, (session) => session.time);
      for (const [i, session] of sessions.entries()) {
        const time = session.respondedAt || session.time;
        data.push({
          id: String(session.id),
          name: FormatDate(time, 'MM/DD'),
          y: session.weight ? +session.weight.toFixed(2) : null,
          x: i
        });
      }
      series.data = data;
      options.series = [series];
      return options;
    }
  };
  const graphs = [symptoms, weight];
  return (
    <SessionGraphs<IEpxHeart>
      url="/data/data_by_range/"
      graphs={graphs}
      {...props}
    />
  );
};

export default EpxHeartGraphs;
